// src/components/badges/Badge.jsx
import React from "react";
import { Link } from "react-router-dom";

/**
 * Responsive "How Certificates Work" page
 * - Hero banner with CTA
 * - Steps explaining the flow
 * - What's on the certificate + share guidance
 * - FAQ + Troubleshooting (LinkedIn share dialog)
 */
export default function Badge() {
  return (
    <div className="bg-light">
      {/* Hero / Banner */}
      <section
        className="position-relative overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, rgba(79,70,229,.10), rgba(147,51,234,.10))",
        }}
        aria-label="Certificate overview banner"
      >
        <div className="container py-5">
          <div className="row align-items-center g-4">
            <div className="col-12 col-lg-7">
              <h1 className="display-6 fw-bold mb-2">
                Earn Shareable Certificates for Your Skills
              </h1>
              <p className="lead text-muted mb-4">
                Take a 10-minute <portal></portal>roficiency test (20 questions). Get an instant,
                downloadable certificate with your name, email, score and
                issuer: <span className="fw-semibold">TechVenture LLC</span>.
              </p>

              <div className="d-flex flex-wrap gap-2">
                <Link to="/proficiencyTest" className="btn btn-primary btn-lg">
                  Start Assessment
                </Link>
                <Link to="/contactUs" className="btn btn-outline-secondary btn-lg">
                  Questions? Contact Us
                </Link>
              </div>

              <div className="mt-3 text-muted small">
                Mobile-friendly • No sign-in required • Download as SVG/PNG
              </div>
            </div>

            <div className="col-12 col-lg-5">
              <div className="card shadow-sm border-0">
                <div className="card-body">
                  <h2 className="h6 text-uppercase text-muted mb-3">
                    What’s Assessed (examples)
                  </h2>
                  <div className="row row-cols-1 row-cols-sm-2 g-3">
                    <div className="col">
                      <div className="p-3 rounded bg-white border h-100">
                        <div className="fw-semibold mb-1">Java</div>
                        <div className="small text-muted">
                          OOP, collections, exceptions, generics, I/O, streams,
                          concurrency, JVM basics.
                        </div>
                      </div>
                    </div>
                    <div className="col">
                      <div className="p-3 rounded bg-white border h-100">
                        <div className="fw-semibold mb-1">JavaScript</div>
                        <div className="small text-muted">
                          Scope, closures, async/await, DOM, modules, array
                          methods, prototypes.
                        </div>
                      </div>
                    </div>
                    <div className="col">
                      <div className="p-3 rounded bg-white border h-100">
                        <div className="fw-semibold mb-1">SQL</div>
                        <div className="small text-muted">
                          Joins, grouping, subqueries, indexing, ACID, window
                          functions.
                        </div>
                      </div>
                    </div>
                    <div className="col">
                      <div className="p-3 rounded bg-white border h-100">
                        <div className="fw-semibold mb-1">Spring / React / Angular</div>
                        <div className="small text-muted">
                          Core APIs, configuration, DI, lifecycle, routing, data
                          binding, components.
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="small text-muted mt-3">
                    Full list varies by quiz. See each quiz page for details.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="container py-5">
        <h2 className="h4 fw-bold mb-4">How the Certificate Works</h2>

        <div className="row g-3 row-cols-1 row-cols-md-3">
          <div className="col">
            <div className="card h-100 shadow-sm border-0">
              <div className="card-body">
                <div className="badge bg-primary rounded-pill mb-3">Step 1</div>
                <h3 className="h6">Choose a quiz</h3>
                <p className="text-muted small mb-0">
                  Pick Java, JavaScript, SQL, Spring, Hibernate, React, Angular,
                  Java 8, Collections, and more.
                </p>
              </div>
            </div>
          </div>

          <div className="col">
            <div className="card h-100 shadow-sm border-0">
              <div className="card-body">
                <div className="badge bg-primary rounded-pill mb-3">Step 2</div>
                <h3 className="h6">Enter your details</h3>
                <p className="text-muted small mb-0">
                  Provide your name (and email, when asked). You’ll get 10
                  minutes to answer 20 questions.
                </p>
              </div>
            </div>
          </div>

          <div className="col">
            <div className="card h-100 shadow-sm border-0">
              <div className="card-body">
                <div className="badge bg-primary rounded-pill mb-3">Step 3</div>
                <h3 className="h6">Get your certificate</h3>
                <p className="text-muted small mb-0">
                  See your score instantly and download a branded certificate
                  (SVG/PNG) with the TechVenture LLC logo.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats / rules */}
        <div className="row g-3 mt-4 row-cols-1 row-cols-md-3">
          <div className="col">
            <div className="p-3 rounded border bg-white h-100">
              <div className="fw-semibold">Timer & Attempts</div>
              <div className="small text-muted">
                10 minutes per attempt. You can retake to improve your score.
              </div>
            </div>
          </div>
          <div className="col">
            <div className="p-3 rounded border bg-white h-100">
              <div className="fw-semibold">Scoring</div>
              <div className="small text-muted">
                20 questions. Final score = correct answers. Passing thresholds
                are shown on each quiz results page.
              </div>
            </div>
          </div>
          <div className="col">
            <div className="p-3 rounded border bg-white h-100">
              <div className="fw-semibold">What’s on the certificate</div>
              <div className="small text-muted">
                Name, (optional) email, quiz title, score/percent, date,
                TechVenture LLC logo and issuer text.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sharing guidance */}
      <section className="container pb-5">
        <div className="row g-4">
          <div className="col-12 col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body">
                <h2 className="h5 fw-semibold">How to Share on LinkedIn</h2>
                <ol className="small text-muted mb-3 ps-3">
                  <li>Finish any quiz and click <em>Download Certificate</em>.</li>
                  <li>
                    Open LinkedIn, start a new post, and use{" "}
                    <strong>“Add a photo”</strong> to attach the saved SVG/PNG.
                  </li>
                  <li>
                    Add a caption (e.g., “Completed the Java Proficiency Test by
                    TechVenture LLC — {`{score}%`}!”) and post.
                  </li>
                </ol>
                <div className="alert alert-secondary small mb-0">
                  <strong>Why did I see a blank pop-up?</strong> The
                  “share-offsite” link only shares a URL, not files. LinkedIn
                  doesn’t allow uploading an image directly from that dialog.
                  Download the certificate first, then create a post and attach
                  the file.
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body">
                <h2 className="h5 fw-semibold">FAQ</h2>
                <div className="accordion" id="badgeFaq">
                  <div className="accordion-item">
                    <h2 className="accordion-header" id="q1">
                      <button
                        className="accordion-button collapsed"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#a1"
                        aria-expanded="false"
                        aria-controls="a1"
                      >
                        Can I retake a quiz?
                      </button>
                    </h2>
                    <div id="a1" className="accordion-collapse collapse" aria-labelledby="q1" data-bs-parent="#badgeFaq">
                      <div className="accordion-body small text-muted">
                        Absolutely. Retake anytime—your new certificate will
                        reflect the latest score.
                      </div>
                    </div>
                  </div>

                  <div className="accordion-item">
                    <h2 className="accordion-header" id="q2">
                      <button
                        className="accordion-button collapsed"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#a2"
                        aria-expanded="false"
                        aria-controls="a2"
                      >
                        Will my email appear on the certificate?
                      </button>
                    </h2>
                    <div id="a2" className="accordion-collapse collapse" aria-labelledby="q2" data-bs-parent="#badgeFaq">
                      <div className="accordion-body small text-muted">
                        If a quiz collects your email, it appears under your
                        name on the certificate. Otherwise, only your name is
                        shown.
                      </div>
                    </div>
                  </div>

                  <div className="accordion-item">
                    <h2 className="accordion-header" id="q3">
                      <button
                        className="accordion-button collapsed"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#a3"
                        aria-expanded="false"
                        aria-controls="a3"
                      >
                        Can I get a PNG instead of SVG?
                      </button>
                    </h2>
                    <div id="a3" className="accordion-collapse collapse" aria-labelledby="q3" data-bs-parent="#badgeFaq">
                      <div className="accordion-body small text-muted">
                        Yes—most browsers and design tools export SVG to PNG.
                        You can also upload the SVG directly to LinkedIn as an
                        image.
                      </div>
                    </div>
                  </div>

                  <div className="accordion-item">
                    <h2 className="accordion-header" id="q4">
                      <button
                        className="accordion-button collapsed"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#a4"
                        aria-expanded="false"
                        aria-controls="a4"
                      >
                        What’s the validity of the certificate?
                      </button>
                    </h2>
                    <div id="a4" className="accordion-collapse collapse" aria-labelledby="q4" data-bs-parent="#badgeFaq">
                      <div className="accordion-body small text-muted">
                        There’s no expiry, but we recommend retesting annually
                        as frameworks and best practices evolve.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-3">
                  <Link to="/proficiencyTest" className="btn btn-primary">
                    Browse Proficiency Tests
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-5 text-center">
        <div className="container">
          <h2 className="h4 fw-bold mb-2">Ready to prove your skills?</h2>
          <p className="text-muted mb-4">
            Complete a short quiz and share your achievement with the world.
          </p>
          <Link to="/proficiencyTest" className="btn btn-success btn-lg">
            Earn a Certificate
          </Link>
        </div>
      </section>
    </div>
  );
}
